import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Checkbox } from '@components/base/inputs';
import { Card, VStack, HStack, Grid, GridItem } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Alert, Spinner, useToast } from '@components/base/feedback';
import { workflowRulesService } from '../../api/workflowRulesService';
import { workflowStepsService } from '../../api/workflowStepsService';
import { tenantsService } from '../../api/tenantsService';
import { StepConfigPanel } from './step-config/StepConfigPanel';
import { getStepTypeLabel, serializeConfig, deserializeConfig } from './step-config/types';
import { parseJsonField } from '../../utils/parseJsonField';
import type { StoreWorkflowRuleRequest, UpdateWorkflowRuleRequest } from '../../api/workflowRulesService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const inputClassName =
  'w-full rounded-md border border-gray-300 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// UI-only rule type. `clone-replace` is serialized as `replace` when sent to
// the backend; it exists solely to drive auto-prefill UX in the form.
type RuleTypeUI = 'activate' | 'inactivate' | 'replace' | 'clone-replace';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkflowRuleFormPage() {
  const { slug, ruleId } = useParams<{ slug: string; ruleId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const slugOrEmpty = slug ?? '';
  const isEdit = !!ruleId;

  // ---- Form state -----------------------------------------------------------

  const [tenantId, setTenantId] = useState('');
  const [ruleType, setRuleType] = useState<RuleTypeUI>('inactivate');
  const [targetStepId, setTargetStepId] = useState('');
  const [isActive, setIsActive] = useState(true);

  const showsReplacementForm = ruleType === 'replace' || ruleType === 'clone-replace';

  // Replacement step data (single record matching StepConfigPanel contract)
  const [replacementValues, setReplacementValues] = useState<Record<string, unknown>>({
    step_type: '',
    name: '',
    execution_mode: 'sync',
    timeout_seconds: '',
    condition: '{}',
    retry_policy: '{}',
    config_iterate_group: '',
    config_iterate_over: '',
    config_iterate_as: '',
  });

  const getReplacementValue = useCallback(
    (field: string) => replacementValues[field],
    [replacementValues],
  );
  const setReplacementValue = useCallback(
    (field: string, value: unknown) => {
      setReplacementValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const [formInitialized, setFormInitialized] = useState(false);

  // ---- Fetch tenants --------------------------------------------------------

  const { data: tenantsResponse, isLoading: isTenantsLoading } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantsService.list(),
  });
  const tenants = tenantsResponse?.data ?? [];

  // ---- Fetch steps ----------------------------------------------------------

  const { data: stepsResponse, isLoading: isStepsLoading } = useQuery({
    queryKey: ['admin', 'workflows', slugOrEmpty, 'steps'],
    queryFn: () => workflowStepsService.list(slugOrEmpty),
    enabled: !!slug,
  });
  const steps = useMemo(() => {
    const raw = stepsResponse?.data ?? [];
    return [...raw].sort((a, b) => a.sort_order - b.sort_order);
  }, [stepsResponse]);

  // ---- Fetch step types -----------------------------------------------------

  const { data: stepTypesResponse } = useQuery({
    queryKey: ['admin', 'workflows', 'step-types'],
    queryFn: () => workflowStepsService.getStepTypes(),
    staleTime: 5 * 60 * 1000,
  });
  const stepTypes = stepTypesResponse?.data ?? [];

  // ---- Fetch existing rule for edit mode ------------------------------------

  const {
    isLoading: isFetchLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['admin', 'workflows', slugOrEmpty, 'rules', ruleId],
    queryFn: () => workflowRulesService.get(slugOrEmpty, ruleId!),
    enabled: isEdit && !!slug,
    select: (response) => {
      if (!formInitialized && response?.data) {
        const rule = response.data;
        setTenantId(rule.tenant_id);
        setRuleType(rule.rule_type);
        setTargetStepId(rule.target_step_id);
        setIsActive(rule.is_active);
        if (rule.replacement_step_data) {
          const stepType = rule.replacement_step_data.step_type;
          const configObj = rule.replacement_step_data.config ?? {};
          const configValues = stepType ? deserializeConfig(stepType, configObj) : {};

          setReplacementValues({
            step_type: stepType,
            name: rule.replacement_step_data.name,
            execution_mode: rule.replacement_step_data.execution_mode ?? 'sync',
            timeout_seconds: rule.replacement_step_data.timeout_seconds != null
              ? String(rule.replacement_step_data.timeout_seconds)
              : '',
            condition: rule.replacement_step_data.condition
              ? JSON.stringify(rule.replacement_step_data.condition, null, 2)
              : '{}',
            retry_policy: rule.replacement_step_data.retry_policy
              ? JSON.stringify(rule.replacement_step_data.retry_policy, null, 2)
              : '{}',
            config_iterate_group: configValues.config_iterate_group ?? '',
            config_iterate_over: configValues.config_iterate_over ?? '',
            config_iterate_as: configValues.config_iterate_as ?? '',
            ...configValues,
          });
        }
        setFormInitialized(true);
      }
      return response;
    },
  });

  // ---- Clone-&-Replace prefill ---------------------------------------------
  // When the user is in `clone-replace` mode and picks (or changes) a target
  // step, auto-populate the replacement form from that step. Does nothing in
  // any other mode. In edit mode we wait for the existing rule to have loaded
  // (`formInitialized`) before running so we don't clobber the loaded rule.
  useEffect(() => {
    if (ruleType !== 'clone-replace') return;
    if (!targetStepId) return;
    if (isEdit && !formInitialized) return;

    const src = steps.find((s) => s.id === targetStepId);
    if (!src) return;

    const stepType = src.step_type;
    const configObj = src.config;
    const configValues = stepType ? deserializeConfig(stepType, configObj) : {};

    // eslint-disable-next-line react-hooks/set-state-in-effect -- Prefill replacement form from the selected target step when entering clone-replace mode or when the target step changes. The source inputs (ruleType, targetStepId, steps) are the trigger; derived form state intentionally follows.
    setReplacementValues({
      step_type: stepType,
      name: `${src.name} (copy)`,
      execution_mode: src.execution_mode,
      timeout_seconds: src.timeout_seconds != null ? String(src.timeout_seconds) : '',
      condition: src.condition ? JSON.stringify(src.condition, null, 2) : '{}',
      retry_policy: src.retry_policy ? JSON.stringify(src.retry_policy, null, 2) : '{}',
      config_iterate_group: configValues.config_iterate_group ?? '',
      config_iterate_over: configValues.config_iterate_over ?? '',
      config_iterate_as: configValues.config_iterate_as ?? '',
      ...configValues,
    });
  }, [ruleType, targetStepId, steps, isEdit, formInitialized]);

  // ---- Mutation -------------------------------------------------------------

  const mutation = useMutation({
    mutationFn: () => {
      let replacementStepData = null;
      if (ruleType === 'replace' || ruleType === 'clone-replace') {
        const stepType = replacementValues.step_type as string;
        replacementStepData = {
          step_type: stepType,
          name: replacementValues.name as string,
          config: stepType ? serializeConfig(stepType, replacementValues) : {},
          execution_mode: (replacementValues.execution_mode as 'sync' | 'async') ?? 'sync',
          timeout_seconds: replacementValues.timeout_seconds ? Number(replacementValues.timeout_seconds) : null,
          condition: parseJsonField(replacementValues.condition),
          retry_policy: parseJsonField(replacementValues.retry_policy),
        };
      }

      // clone-replace is purely UX sugar — always serialized as 'replace' for the backend.
      const wireRuleType: 'activate' | 'inactivate' | 'replace' = ruleType === 'clone-replace' ? 'replace' : ruleType;

      if (isEdit) {
        const payload: UpdateWorkflowRuleRequest = {
          tenant_id: tenantId,
          rule_type: wireRuleType,
          target_step_id: targetStepId,
          replacement_step_data: replacementStepData,
          is_active: isActive,
        };
        return workflowRulesService.update(slugOrEmpty, ruleId!, payload);
      }

      const payload: StoreWorkflowRuleRequest = {
        tenant_id: tenantId,
        rule_type: wireRuleType,
        target_step_id: targetStepId,
        replacement_step_data: replacementStepData,
        is_active: isActive,
      };
      return workflowRulesService.create(slugOrEmpty, payload);
    },
    onSuccess: () => {
      // Invalidate the whole workflow subtree so both the rules list and the
      // definition (which carries `rules_count`) are refreshed.
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'workflows', slugOrEmpty],
      });
      toast({
        variant: 'success',
        title: isEdit ? 'Rule updated successfully.' : 'Rule created successfully.',
      });
      navigate(`/admin/workflows/${slugOrEmpty}`);
    },
    onError: (err: unknown) => {
      toast({
        variant: 'error',
        title: err instanceof Error ? err.message : 'Failed to save rule.',
      });
    },
  });

  // ---- Validation -----------------------------------------------------------

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    setValidationError(null);

    if (!tenantId) {
      setValidationError('Please select a tenant.');
      return;
    }
    if (!targetStepId) {
      setValidationError('Please select a target step.');
      return;
    }
    if (ruleType === 'replace' || ruleType === 'clone-replace') {
      if (!replacementValues.step_type) {
        setValidationError('Please select a replacement step type.');
        return;
      }
      if (!(replacementValues.name as string).trim()) {
        setValidationError('Please enter a replacement step name.');
        return;
      }
      const conditionStr = replacementValues.condition as string;
      if (conditionStr && conditionStr.trim() !== '' && conditionStr.trim() !== '{}') {
        try {
          JSON.parse(conditionStr);
        } catch {
          setValidationError('Condition must be valid JSON.');
          return;
        }
      }
      const retryStr = replacementValues.retry_policy as string;
      if (retryStr && retryStr.trim() !== '' && retryStr.trim() !== '{}') {
        try {
          JSON.parse(retryStr);
        } catch {
          setValidationError('Retry policy must be valid JSON.');
          return;
        }
      }
    }

    mutation.mutate();
  }, [tenantId, targetStepId, ruleType, replacementValues, mutation]);

  // ---- Breadcrumbs ----------------------------------------------------------

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows', href: '/admin/workflows' },
    { label: slugOrEmpty, href: `/admin/workflows/${slugOrEmpty}` },
    { label: isEdit ? 'Edit Rule' : 'Create Rule' },
  ], [slugOrEmpty, isEdit]);

  // ---- Loading state --------------------------------------------------------

  if (isEdit && isFetchLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading rule...</Text>
      </VStack>
    );
  }

  // ---- Fetch error ----------------------------------------------------------

  if (isEdit && fetchError) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          {breadcrumbs.map((item, index) => (
            <Breadcrumb.Item
              key={item.label}
              href={index < breadcrumbs.length - 1 ? item.href : undefined}
              isCurrent={index === breadcrumbs.length - 1}
            >
              {item.label}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <Alert variant="error" title="Failed to load rule">
          {fetchError instanceof Error
            ? fetchError.message
            : 'An unexpected error occurred while loading the workflow rule.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Main render ----------------------------------------------------------

  return (
    <VStack gap={6}>
      <Breadcrumb>
        {breadcrumbs.map((item, index) => (
          <Breadcrumb.Item
            key={item.label}
            href={index < breadcrumbs.length - 1 ? item.href : undefined}
            isCurrent={index === breadcrumbs.length - 1}
          >
            {item.label}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      <Text as="h1" size="2xl" weight="bold">
        {isEdit ? 'Edit Rule' : 'Create Rule'}
      </Text>

      {validationError && (
        <Alert
          variant="error"
          title="Validation error"
          dismissible
          onDismiss={() => { setValidationError(null); }}
        >
          {validationError}
        </Alert>
      )}

      {mutation.isError && (
        <Alert variant="error" title="Save failed">
          {mutation.error instanceof Error
            ? mutation.error.message
            : 'An unexpected error occurred.'}
        </Alert>
      )}

      <Card>
        <Card.Header title="Rule Configuration" />
        <Card.Content>
          <VStack gap={4}>
            <Grid columns={2} gap={4}>
              {/* Tenant */}
              <GridItem>
                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Tenant
                  </Text>
                  <select
                    value={tenantId}
                    onChange={(e) => { setTenantId(e.target.value); }}
                    className={inputClassName}
                    disabled={isTenantsLoading}
                  >
                    <option value="">Select a tenant...</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </VStack>
              </GridItem>

              {/* Rule Type */}
              <GridItem>
                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Rule Type
                  </Text>
                  <select
                    value={ruleType}
                    onChange={(e) => { setRuleType(e.target.value as RuleTypeUI); }}
                    className={inputClassName}
                  >
                    <option value="activate">Activate</option>
                    <option value="inactivate">Inactivate</option>
                    <option value="replace">Replace</option>
                    <option value="clone-replace">Clone & Replace</option>
                  </select>
                </VStack>
              </GridItem>

              {/* Target Step */}
              <GridItem>
                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Target Step
                  </Text>
                  <select
                    value={targetStepId}
                    onChange={(e) => { setTargetStepId(e.target.value); }}
                    className={inputClassName}
                    disabled={isStepsLoading}
                  >
                    <option value="">Select a step...</option>
                    {steps.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.sort_order}. {step.name}
                      </option>
                    ))}
                  </select>
                </VStack>
              </GridItem>

              {/* Active */}
              <GridItem>
                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Active
                  </Text>
                  <div className="pt-2">
                    <Checkbox
                      label="Active"
                      checked={isActive}
                      onChange={(e) => { setIsActive(e.target.checked); }}
                    />
                  </div>
                </VStack>
              </GridItem>
            </Grid>
          </VStack>
        </Card.Content>
      </Card>

      {/* Replacement step data fields */}
      {showsReplacementForm && (
        <>
          {/* Card 1: Step Information */}
          <Card>
            <Card.Header title="Step Information" />
            <Card.Content>
              <Grid columns={2} gap={4}>
                {/* Name */}
                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Name
                    </Text>
                    <input
                      type="text"
                      value={replacementValues.name as string}
                      onChange={(e) => { setReplacementValue('name', e.target.value); }}
                      className={inputClassName}
                      placeholder="e.g. Validate Claim Data"
                    />
                  </VStack>
                </GridItem>

                {/* Step Type */}
                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Step Type
                    </Text>
                    <select
                      value={replacementValues.step_type as string}
                      onChange={(e) => { setReplacementValue('step_type', e.target.value); }}
                      className={inputClassName}
                    >
                      <option value="">Select a step type...</option>
                      {stepTypes.map((type) => (
                        <option key={type} value={type}>
                          {getStepTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </VStack>
                </GridItem>

                {/* Execution Mode */}
                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Execution Mode
                    </Text>
                    <select
                      value={replacementValues.execution_mode as string}
                      onChange={(e) => { setReplacementValue('execution_mode', e.target.value); }}
                      className={inputClassName}
                    >
                      <option value="sync">Synchronous</option>
                      <option value="async">Asynchronous</option>
                    </select>
                  </VStack>
                </GridItem>
              </Grid>
            </Card.Content>
          </Card>

          {/* Card 2: Iteration */}
          <Card>
            <Card.Header title="Iteration" />
            <Card.Content>
              <Grid columns={3} gap={4}>
                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Iterate Group
                    </Text>
                    <Text size="xs" color="muted">
                      Groups consecutive steps into an iteration block
                    </Text>
                    <input
                      type="text"
                      value={replacementValues.config_iterate_group as string}
                      onChange={(e) => { setReplacementValue('config_iterate_group', e.target.value); }}
                      className={inputClassName}
                      placeholder="e.g. per_garantie"
                    />
                  </VStack>
                </GridItem>

                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Iterate Over
                    </Text>
                    <Text size="xs" color="muted">
                      Context key pointing to an array to iterate over. Supports dot notation.
                    </Text>
                    <input
                      type="text"
                      value={replacementValues.config_iterate_over as string}
                      onChange={(e) => { setReplacementValue('config_iterate_over', e.target.value); }}
                      className={inputClassName}
                      placeholder="e.g. codes_garanties"
                    />
                  </VStack>
                </GridItem>

                <GridItem>
                  <VStack gap={1}>
                    <Text size="sm" color="muted" weight="medium">
                      Iterate As
                    </Text>
                    <Text size="xs" color="muted">
                      Context key under which the current iteration item is injected.
                    </Text>
                    <input
                      type="text"
                      value={replacementValues.config_iterate_as as string}
                      onChange={(e) => { setReplacementValue('config_iterate_as', e.target.value); }}
                      className={inputClassName}
                      placeholder="e.g. current_code_garantie"
                    />
                  </VStack>
                </GridItem>
              </Grid>
            </Card.Content>
          </Card>

          {/* Card 3: Advanced */}
          <Card>
            <Card.Header title="Advanced" />
            <Card.Content>
              <VStack gap={4}>
                <Grid columns={2} gap={4}>
                  <GridItem>
                    <VStack gap={1}>
                      <Text size="sm" color="muted" weight="medium">
                        Timeout (seconds)
                      </Text>
                      <input
                        type="number"
                        value={replacementValues.timeout_seconds as string}
                        onChange={(e) => { setReplacementValue('timeout_seconds', e.target.value); }}
                        className={inputClassName}
                        placeholder="e.g. 30"
                      />
                    </VStack>
                  </GridItem>
                </Grid>

                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Condition
                  </Text>
                  <Text size="xs" color="muted">
                    JSON condition that must be met for this step to execute
                  </Text>
                  <textarea
                    value={replacementValues.condition as string}
                    onChange={(e) => { setReplacementValue('condition', e.target.value); }}
                    rows={6}
                    className={`${inputClassName} font-mono`}
                    placeholder="{}"
                  />
                </VStack>

                <VStack gap={1}>
                  <Text size="sm" color="muted" weight="medium">
                    Retry Policy
                  </Text>
                  <Text size="xs" color="muted">
                    JSON retry configuration
                  </Text>
                  <textarea
                    value={replacementValues.retry_policy as string}
                    onChange={(e) => { setReplacementValue('retry_policy', e.target.value); }}
                    rows={6}
                    className={`${inputClassName} font-mono`}
                    placeholder="{}"
                  />
                </VStack>
              </VStack>
            </Card.Content>
          </Card>

          {/* Card 4: Step Configuration (dynamic per step type) */}
          <StepConfigPanel
            stepType={replacementValues.step_type as string ?? ''}
            getValue={getReplacementValue}
            setValue={setReplacementValue}
          />
        </>
      )}

      {/* Actions */}
      <HStack gap={2} justify="end">
        <Button
          variant="secondary"
          onClick={() => { navigate(`/admin/workflows/${slugOrEmpty}`); }}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={mutation.isPending}
        >
          {isEdit ? 'Update Rule' : 'Create Rule'}
        </Button>
      </HStack>
    </VStack>
  );
}
